import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { EMPTY, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { displaySessionExpirePage, login, logout, userAuthenticated } from '@capital-access/common/actions';
import { AppSettingsService } from '@capital-access/common/settings';
import {
  initializeImpersonation,
  selectIsImpersonating,
  selectIsConsultantImpersonation,
  stopImpersonation
} from '@capital-access/common/user';
import { ImpersonationStopReason } from '@capital-access/common/user';
import { SESSION_EXPIRE_PAGE_ROUTE } from '@capital-access/common/utils';
import { userDataLoaded } from './auth.actions';
import { getAuthorizeWithPopUpStatus, getUserEmail } from './auth.selectors';
import { completeAuthorizeWithPopUp, failedAuthorizeWithPopUp } from './auth-popup.actions';
import { AuthorizeWithPopUpStatus } from './models/authorize-pop-up-status.model';
import { Settings } from './models/bny-settings.model';
import { mapUserData } from './models/user-data-mapper.util';
import { Log } from '@capital-access/common/logging';

@Injectable()
export class AuthEffects {
  settings = this.settingsService.getSettings<Settings>();

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(login),
        tap(() => {
          this.store
            .select(getUserEmail)
            .pipe(take(1))
            .subscribe(email => {
              if (email?.includes(this.settings.bnySettings.emailDoamin)) {
                this.oidcSecurityService.authorize(undefined, {
                  customParams: { login_hint: this.settings.bnySettings.hintUrl }
                });
              } else {
                this.oidcSecurityService.authorize();
              }
            });
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        switchMap(() =>
          this.store.select(selectIsImpersonating).pipe(
            take(1),
            switchMap(isImpersonating => {
              if (isImpersonating) {
                return this.store.select(selectIsConsultantImpersonation).pipe(
                  take(1),
                  tap(isConsultantImpersonation => {
                    this.store.dispatch(
                      stopImpersonation({
                        reason: ImpersonationStopReason.Logout,
                        isConsultantImpersonation
                      })
                    );
                  }),
                  switchMap(() => EMPTY)
                );
              } else {
                return this.oidcSecurityService.userData$.pipe(
                  take(1),
                  switchMap(({ userData }) => {
                    const oidcId = userData?.sub;
                    if (!oidcId) {
                      return this.oidcSecurityService.logoff();
                    }
                    // Clear cache with SAM ID array before logging off
                    return this.http.delete(`/api/identity/v1/cache/all`, { body: [oidcId] }).pipe(
                      catchError(() => {
                        return of(null);
                      }), // Continue with logout even if cache clear fails
                      switchMap(() => this.oidcSecurityService.logoff())
                    );
                  })
                );
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  loadUserData$ = createEffect(() =>
    this.oidcSecurityService.userData$.pipe(
      filter(({ userData }) => !!userData),
      map(({ userData }) => mapUserData(userData)),
      map(userData => userDataLoaded({ userData }))
    )
  );

  userAuthenticated$ = createEffect(() =>
    this.oidcSecurityService.isAuthenticated$.pipe(
      filter(result => !!result && result.isAuthenticated),
      take(1),
      switchMap(() => {
        const hasImpersonationData = this.hasImpersonationDataInStorage();

        if (hasImpersonationData) {
          this.handleImpersonation();
          return of(userAuthenticated({ isInitializingImpersonation: true }));
        }

        return of(userAuthenticated({ isInitializingImpersonation: false }));
      })
    )
  );

  authorizeWithPopUp$ = createEffect(() =>
    this.store.select(getAuthorizeWithPopUpStatus).pipe(
      //To avoid multiple pop-ups if the page sends more than one request
      distinctUntilChanged(),
      filter(status => status === AuthorizeWithPopUpStatus.Initiated),
      switchMap(() => {
        /**
         * Please note that popup might not work correctly when running app locally
         * if you have Redux Devtools browser extension installed
         * that uses the same mechanism for cross-origin communication via Window.postMessage()
         */
        return this.oidcSecurityService.authorizeWithPopUp().pipe(
          map(() => {
            //add refesh page after login with popup
            window.location.reload();
            return completeAuthorizeWithPopUp();
          }),
          catchError(error => of(failedAuthorizeWithPopUp({ error })))
        );
      })
    )
  );

  displaySessionExpirePage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(displaySessionExpirePage),
        switchMap(() =>
          this.store.select(selectIsConsultantImpersonation).pipe(
            take(1),
            tap(isConsultantImpersonation => {
              if (isConsultantImpersonation) {
                this.clearConsultantImpersonationData();
              }
              this.router.navigate([this.sessionExpirePageRoute]);
            })
          )
        )
      ),
    { dispatch: false }
  );

  private getStoredUserId(key: string): number | null {
    const value = localStorage.getItem(key);
    if (!value || value === 'null' || value === 'undefined') {
      return null;
    }
    const userId = Number(value);
    return isNaN(userId) ? null : userId;
  }

  private getImpersonationData(): {
    isImpersonating: boolean;
    targetUserId: number | null;
    isConsultantImpersonation: boolean;
  } {
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const targetUserId = this.getStoredUserId('targetUserId');
    const isConsultantImpersonation = localStorage.getItem('isConsultantImpersonation') === 'true';

    return { isImpersonating, targetUserId, isConsultantImpersonation };
  }

  private hasImpersonationDataInStorage(): boolean {
    const { isImpersonating, targetUserId } = this.getImpersonationData();
    return isImpersonating && !!targetUserId;
  }

  private handleImpersonation(): void {
    const { isImpersonating, targetUserId, isConsultantImpersonation } = this.getImpersonationData();

    if (!isImpersonating || !targetUserId) {
      return;
    }

    this.store.dispatch(
      initializeImpersonation({
        targetUserId,
        isConsultantImpersonation
      })
    );
  }

  private clearConsultantImpersonationData(): void {
    localStorage.removeItem('targetUserId');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('isConsultantImpersonation');
    localStorage.removeItem('impersonationHistoryId');
    localStorage.removeItem('impersonationFreshStart');
  }

  constructor(
    private actions$: Actions,
    private oidcSecurityService: OidcSecurityService,
    private store: Store,
    private router: Router,
    private settingsService: AppSettingsService,
    private http: HttpClient,
    @Inject(SESSION_EXPIRE_PAGE_ROUTE) private sessionExpirePageRoute: string
  ) {}
}
